import Button from 'antd/lib/button';
import Model from 'antd/lib/modal';
import Row from 'antd/lib/row';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import React, { useRef } from 'react';

function ViewOrderModel({ isOpen, order, handleViewModelCloseClick }) {
  const contentRef = useRef();

  const {
    orderId,
    companyName,
    gstNumber,
    phoneNumber,
    transportName,
    salesPerson,
    orderNo,
    orderStatus,
    orderDate,
    productItems,
    orderAmount,
    gstOrderAmount,
    totalOrderAmountWithGST,
    productSpareItems,
    note,
  } = order;
  const companyAddress = order.companyAddress
    ? JSON.parse(order.companyAddress)
    : '';

  const columns = [
    { header: 'Product Name', dataKey: 'productName' },
    { header: 'M Number', dataKey: 'modelNo' },
    { header: 'Qty', dataKey: 'productQty' },
    { header: 'GST', dataKey: 'gstTax' },
    { header: 'Rate', dataKey: 'productAmount' },
    { header: 'Taxable Amt', dataKey: 'productTotalAmount' },
    { header: 'GST Amt', dataKey: 'gstAmount' },
    { header: 'Net Amt', dataKey: 'totalAMountWithGST' },
  ];

  const getRowData = () => {
    let rows = [];

    if (productItems) {
      rows = [...rows, ...productItems];
    }

    if (productSpareItems) {
      rows = [...rows, ...productSpareItems];
    }

    rows = [
      ...rows,
      {
        productName: 'Total',
        productTotalAmount: orderAmount,
        totalAMountWithGST: totalOrderAmountWithGST,
        gstAmount: gstOrderAmount,
      },
    ];
    return rows;
  };

  const generatePdfFromHtml = async () => {
    const doc = new jsPDF({
      orientation: 'portrait',
      format: 'a4',
    });

    const content = contentRef.current;
    const canvas = await html2canvas(content);
    const imgData = canvas.toDataURL('image/png');
    const imgProps = doc.getImageProperties(imgData);
    const pdfWidth = doc.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width - 20;

    doc.addImage(imgData, 'PNG', 10, 0, pdfWidth, pdfHeight);

    const rows = getRowData();

    const tableStartY = pdfHeight + 10;
    doc.autoTable({
      columns: columns,
      body: rows,
      startY: tableStartY,
      margin: { horizontal: 10 },
      styles: {
        cellPadding: 2,
        fontSize: 10,
        lineColor: [44, 62, 80],
        lineWidth: 0.5,
        textColor: [0, 0, 0],
      },
    });

    doc.save(
      `${companyName.split(' ').join('_')}_${moment.utc().format('DD_MM_YYYY')}.pdf`,
    );
  };

  const printPdf = async () => {
    const doc = new jsPDF({
      orientation: 'portrait',
      format: 'a4',
    });

    const content = contentRef.current;
    const canvas = await html2canvas(content);
    const imgData = canvas.toDataURL('image/png');
    const imgProps = doc.getImageProperties(imgData);
    const pdfWidth = doc.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width - 20;

    doc.addImage(imgData, 'PNG', 10, 0, pdfWidth - 20, pdfHeight);

    const rows = getRowData();

    const tableStartY = pdfHeight + 10;
    doc.autoTable({
      columns: columns,
      body: rows,
      startY: tableStartY,
      margin: { horizontal: 10 },
      styles: {
        cellPadding: 2,
        fontSize: 10,
        lineColor: [44, 62, 80],
        lineWidth: 0.5,
        textColor: [0, 0, 0],
      },
    });

    const pdfBlob = doc.output('bloburl');
    const newWindow = window.open(pdfBlob, '_blank');
    if (newWindow) {
      newWindow.onload = () => {
        newWindow.print();
      };
    }
  };

  return (
    <div>
      <Model
        open={isOpen}
        onCancel={handleViewModelCloseClick}
        closable={false}
        footer={null}
        width={1000}
      >
        <div className='px-8 pb-6'>
          {/* Header */}
          <div className='flex items-center justify-between mb-6'>
            <div className='flex items-center gap-3'>
              <div className='p-2 rounded-lg shadow bg-gradient-to-r from-blue-500 to-purple-600'></div>
              <h2 className='text-2xl font-bold text-text m-0'>
                Order Details
              </h2>
            </div>
            <div className='text-sm text-muted'>#{orderNo}</div>
          </div>

          <div
            className='flex flex-row flex-wrap gap-y-3 pb-5'
            id='order-details'
            ref={contentRef}
          >
            <Row className='w-full grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='bg-surface border border-border rounded-lg p-4 shadow-sm'>
                <div className='text-muted text-xs mb-1'>Company Name</div>
                <div className='text-text font-semibold'>{companyName}</div>
              </div>
              <div className='bg-surface border border-border rounded-lg p-4 shadow-sm'>
                <div className='text-muted text-xs mb-1'>Order Number</div>
                <div className='text-text'>{orderId || orderNo}</div>
              </div>
              <div className='bg-surface border border-border rounded-lg p-4 shadow-sm'>
                <div className='text-muted text-xs mb-1'>GST Number</div>
                <div className='text-text'>{gstNumber}</div>
              </div>
              <div className='bg-surface border border-border rounded-lg p-4 shadow-sm'>
                <div className='text-muted text-xs mb-1'>Phone Number</div>
                <div className='text-text'>{phoneNumber}</div>
              </div>
              <div className='bg-surface border border-border rounded-lg p-4 shadow-sm'>
                <div className='text-muted text-xs mb-1'>Order Status</div>
                <div className='text-text'>{orderStatus}</div>
              </div>
              <div className='bg-surface border border-border rounded-lg p-4 shadow-sm'>
                <div className='text-muted text-xs mb-1'>Order Date</div>
                <div className='text-text'>
                  {moment.utc(orderDate).format('DD/MM/YYYY HH:mm A')}
                </div>
              </div>
              <div className='bg-surface border border-border rounded-lg p-4 shadow-sm'>
                <div className='text-muted text-xs mb-1'>Transport Name</div>
                <div className='text-text'>{transportName || '-'}</div>
              </div>
              <div className='bg-surface border border-border rounded-lg p-4 shadow-sm'>
                <div className='text-muted text-xs mb-1'>Sales Person</div>
                <div className='text-text'>{salesPerson || '-'}</div>
              </div>
              <div className='bg-surface border border-border rounded-lg p-4 shadow-sm md:col-span-2'>
                <div className='text-muted text-xs mb-1'>Address</div>
                {companyAddress && (
                  <div className='text-text'>
                    {companyAddress.StreetAddress +
                      ' ' +
                      companyAddress.City +
                      ' ' +
                      companyAddress.STATE +
                      ' ' +
                      companyAddress.Country +
                      ' ' +
                      companyAddress.ZipCode}
                  </div>
                )}
              </div>
              {note ? (
                <div className='bg-surface border border-border rounded-lg p-4 shadow-sm md:col-span-2'>
                  <div className='text-muted text-xs mb-1'>Order Note</div>
                  <div className='text-text'>{note}</div>
                </div>
              ) : null}
            </Row>
          </div>
          <Row className='w-full'>
            <div className='w-full overflow-hidden bg-surface border border-border rounded-lg shadow'>
              <table className='w-full border-collapse'>
                <thead className='bg-bg'>
                  <tr className='text-left text-text'>
                    <th className='px-3 py-2 border-border border-r'>
                      Product Name
                    </th>
                    <th className='px-3 py-2 border-border border-r'>
                      M Number
                    </th>
                    <th className='px-3 py-2 border-border border-r'>Qty</th>
                    <th className='px-3 py-2 border-border border-r'>GST</th>
                    <th className='px-3 py-2 border-border border-r'>Rate</th>
                    <th className='px-3 py-2 border-border border-r'>
                      Taxable Amt
                    </th>
                    <th className='px-3 py-2 border-border border-r'>
                      GST Amt
                    </th>
                    <th className='px-3 py-2'>Net Amt</th>
                  </tr>
                </thead>
                <tbody>
                  {productItems &&
                    productItems.map((item, idx) => (
                      <tr
                        key={`p-${idx}`}
                        className={`${idx % 2 === 0 ? 'bg-white' : 'bg-bg'}`}
                      >
                        <td className='px-3 py-2 border-border border-r'>
                          {item.productName}
                        </td>
                        <td className='px-3 py-2 border-border border-r'>
                          {item.modelNo}
                        </td>
                        <td className='px-3 py-2 border-border border-r'>
                          {item.productQty}
                        </td>
                        <td className='px-3 py-2 border-border border-r'>
                          {item.gstTax}
                        </td>
                        <td className='px-3 py-2 border-border border-r'>
                          {item.productAmount}
                        </td>
                        <td className='px-3 py-2 border-border border-r'>
                          {item.productTotalAmount}
                        </td>
                        <td className='px-3 py-2 border-border border-r'>
                          {item.gstAmount}
                        </td>
                        <td className='px-3 py-2'>{item.totalAMountWithGST}</td>
                      </tr>
                    ))}
                  {productSpareItems &&
                    productSpareItems.map((item, idx) => (
                      <tr
                        key={`s-${idx}`}
                        className={`${(productItems?.length || 0) + (idx % 2) === 0 ? 'bg-white' : 'bg-bg'}`}
                      >
                        <td className='px-3 py-2 border-border border-r'>
                          {item.productName}
                        </td>
                        <td className='px-3 py-2 border-border border-r'>
                          {item.modelNo}
                        </td>
                        <td className='px-3 py-2 border-border border-r'>
                          {item.productQty}
                        </td>
                        <td className='px-3 py-2 border-border border-r'>
                          {item.gstTax}
                        </td>
                        <td className='px-3 py-2 border-border border-r'>
                          {item.productAmount}
                        </td>
                        <td className='px-3 py-2 border-border border-r'>
                          {item.productTotalAmount}
                        </td>
                        <td className='px-3 py-2 border-border border-r'>
                          {item.gstAmount}
                        </td>
                        <td className='px-3 py-2'>{item.totalAMountWithGST}</td>
                      </tr>
                    ))}
                  <tr className='font-bold bg-bg'>
                    <td
                      colSpan={5}
                      className='px-3 py-2 text-center border-border border-r'
                    >
                      Total
                    </td>
                    <td className='px-3 py-2 border-border border-r'>
                      {orderAmount}
                    </td>
                    <td className='px-3 py-2 border-border border-r'>
                      {gstOrderAmount}
                    </td>
                    <td className='px-3 py-2'>{totalOrderAmountWithGST}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Row>
        </div>
        <Row className='flex flex-row justify-end gap-3 px-8 pb-4'>
          <Button onClick={handleViewModelCloseClick}>Close</Button>
          <Button
            type='primary'
            onClick={generatePdfFromHtml}
          >
            Download
          </Button>
          <Button
            type='primary'
            onClick={printPdf}
          >
            Print
          </Button>
        </Row>
      </Model>
    </div>
  );
}

export default ViewOrderModel;
